/**
 * Les profils des éditeurs courants du mode `?demo` (ARTO-135) : Shopify,
 * WooCommerce, Brevo et Mailchimp, tels que l'API les verse à la bibliothèque
 * (`apps/api/src/reprise/profils-editeurs.ts`). Les règles en sont la copie
 * fidèle, pour que la bibliothèque et les suggestions d'un lot montrent ce que
 * l'exploitant verra : colonnes lues en repli, dictionnaire réduit à une valeur
 * par défaut, constante, filtres « n'est pas vide » et « est différente de ».
 */
import type { ChampRegle, ProfilComplet, Regles } from './correspondance.js';

type Transformations = ChampRegle['transformations'];

const ref = (champ: string) => ({ champ, role: null, parent: false });

/** Un champ lu dans le premier en-tête, puis, s'il est vide, dans les suivants. */
function champ(entetes: readonly string[], transformations: Transformations = []): ChampRegle {
  const [premier, ...reste] = entetes;
  return {
    sources: premier === undefined ? [] : [ref(premier)],
    separateur: ' ',
    transformations: [
      ...reste.map((entete) => ({ type: 'si_vide' as const, source: ref(entete) })),
      ...transformations,
    ],
  };
}

const JOUR: Transformations = [
  { type: 'scinder', separateur: ' ', index: 0 },
  { type: 'date', format: 'JJ/MM/AAAA' },
];
const OUI_SI_RENSEIGNE: Transformations = [{ type: 'dictionnaire', valeurs: {}, defaut: 'oui' }];
const PRIX: Transformations = [{ type: 'montant', decimal: '.' }];
const MINUSCULES: Transformations = [{ type: 'minuscules' }];

function regles(
  nature: 'oeuvres' | 'contacts',
  role: string,
  colonnes: Record<string, ChampRegle>,
  filtres: Regles['natures'][string]['filtres'] = [],
): Regles {
  return {
    sources: [{ role, feuille: null, racine: null }],
    jointures: [],
    deplier: null,
    filtres: [],
    natures: { [nature]: { colonnes, cleOrigine: null, filtres, dedoublonner: null } },
  };
}

const mailchimpCommun = (): Record<string, ChampRegle> => ({
  refOrigine: champ(['Email Address'], MINUSCULES),
  prenom: champ(['First Name']),
  nom: champ(['Last Name']),
  email: champ(['Email Address'], MINUSCULES),
  telephones: champ(['Phone Number']),
  notes: champ(['NOTES']),
  dateConsentement: champ(['CONFIRM_TIME', 'OPTIN_TIME'], JOUR),
  retraitConsentement: champ(['UNSUB_TIME'], JOUR),
});

export const ID_PROFIL_MAILCHIMP_ABONNES = 'profil-demo-mailchimp-abonnes';
export const ID_PROFIL_MAILCHIMP_DESABONNES = 'profil-demo-mailchimp-desabonnes';

/** Les en-têtes de l'export Mailchimp des désabonnés : toute la signature des abonnés, et plus. */
export const SIGNATURE_MAILCHIMP_ABONNES = [
  'Email Address',
  'OPTIN_TIME',
  'CONFIRM_TIME',
  'MEMBER_RATING',
  'LEID',
  'EUID',
];
export const SIGNATURE_MAILCHIMP_DESABONNES = [
  'Email Address',
  'OPTIN_TIME',
  'CONFIRM_TIME',
  'LEID',
  'EUID',
  'UNSUB_TIME',
  'UNSUB_CAMPAIGN_TITLE',
];

interface Def {
  id: string;
  nom: string;
  application: string;
  nature: 'oeuvres' | 'contacts';
  signature: string[];
  regles: Regles;
}

const DEFS: Def[] = [
  {
    id: 'profil-demo-shopify-produits',
    nom: 'Shopify (produits)',
    application: 'Shopify, export CSV des produits',
    nature: 'oeuvres',
    signature: ['Title', 'URL handle', 'Vendor', 'Published on online store', 'Option1 name'],
    regles: regles(
      'oeuvres',
      'produits',
      {
        titre: champ(['Title']),
        artiste: champ(['Vendor']),
        numeroInventaire: champ(['SKU', 'Variant SKU']),
        prixPublic: champ(['Price', 'Variant Price'], PRIX),
        descriptionFr: champ(['Description', 'Body (HTML)']),
        etiquettes: champ(['Tags']),
        visibleSurLeSite: champ(
          ['Status'],
          [{ type: 'dictionnaire', valeurs: { active: 'oui' }, defaut: 'non' }],
        ),
        refOrigine: champ(['URL handle', 'Handle']),
      },
      [{ source: ref('Title'), operateur: 'non_vide', valeur: '' }],
    ),
  },
  {
    id: 'profil-demo-shopify-clients',
    nom: 'Shopify (clients)',
    application: 'Shopify, export CSV des clients',
    nature: 'contacts',
    signature: [
      'First Name',
      'Last Name',
      'Email',
      'Accepts Email Marketing',
      'Default Address Zip',
    ],
    regles: regles('contacts', 'clients', {
      refOrigine: champ(['Email'], MINUSCULES),
      prenom: champ(['First Name']),
      nom: champ(['Last Name', 'Default Address Company']),
      raisonSociale: champ(['Default Address Company']),
      email: champ(['Email'], MINUSCULES),
      telephones: champ(['Phone', 'Default Address Phone']),
      adresse: champ(['Default Address Address1']),
      complementAdresse: champ(['Default Address Address2']),
      codePostal: champ(['Default Address Zip']),
      ville: champ(['Default Address City']),
      pays: champ(['Default Address Country Code']),
      etiquettes: champ(['Tags']),
      notes: champ(['Note']),
      consentement: champ(
        ['Accepts Email Marketing'],
        [{ type: 'dictionnaire', valeurs: { yes: 'oui' }, defaut: 'non' }],
      ),
    }),
  },
  {
    id: 'profil-demo-woocommerce-produits',
    nom: 'WooCommerce (produits)',
    application: 'WooCommerce, export CSV des produits',
    nature: 'oeuvres',
    signature: ['ID', 'Type', 'SKU', 'Name', 'Regular price', 'Tax class', 'Parent'],
    regles: regles(
      'oeuvres',
      'produits',
      {
        titre: champ(['Name']),
        artiste: champ([]),
        numeroInventaire: champ(['SKU']),
        prixPublic: champ(['Regular price'], PRIX),
        descriptionFr: champ(['Description', 'Short description']),
        etiquettes: champ(['Tags']),
        visibleSurLeSite: champ(
          ['Published'],
          [{ type: 'dictionnaire', valeurs: { '1': 'oui' }, defaut: 'non' }],
        ),
        refOrigine: champ(['ID']),
      },
      [{ source: ref('Type'), operateur: 'different', valeur: 'variation' }],
    ),
  },
  {
    id: 'profil-demo-woocommerce-clients',
    nom: 'WooCommerce (clients, Analytique)',
    application: 'WooCommerce, CSV de la page Analytique > Clients (en-têtes à confirmer)',
    nature: 'contacts',
    signature: ['Name', 'Username', 'Last Active', 'Date Registered', 'AOV'],
    regles: regles('contacts', 'clients', {
      refOrigine: champ(['Email'], MINUSCULES),
      nom: champ(['Name']),
      email: champ(['Email'], MINUSCULES),
      codePostal: champ(['Postal Code']),
      ville: champ(['City']),
      pays: champ(['Country / Region']),
    }),
  },
  {
    id: 'profil-demo-brevo-contacts',
    nom: 'Brevo (contacts)',
    application: 'Brevo, export CSV des contacts (en-têtes à confirmer)',
    nature: 'contacts',
    signature: ['EMAIL', 'BLACKLISTED', 'SMS'],
    regles: regles('contacts', 'contacts', {
      refOrigine: champ(['EMAIL'], MINUSCULES),
      prenom: champ(['FIRSTNAME', 'PRENOM']),
      nom: champ(['LASTNAME', 'NOM']),
      email: champ(['EMAIL'], MINUSCULES),
      telephones: champ(['SMS']),
      consentement: champ(
        ['BLACKLISTED', 'BLOCKLISTED'],
        [{ type: 'dictionnaire', valeurs: { false: '', '0': '', non: '', no: '' }, defaut: 'non' }],
      ),
    }),
  },
  {
    id: ID_PROFIL_MAILCHIMP_ABONNES,
    nom: 'Mailchimp (abonnés)',
    application: 'Mailchimp, export de l’audience : fichier des abonnés (CSV)',
    nature: 'contacts',
    signature: SIGNATURE_MAILCHIMP_ABONNES,
    regles: regles('contacts', 'abonnes', {
      ...mailchimpCommun(),
      consentement: champ(['CONFIRM_TIME', 'OPTIN_TIME'], OUI_SI_RENSEIGNE),
    }),
  },
  {
    id: ID_PROFIL_MAILCHIMP_DESABONNES,
    nom: 'Mailchimp (désabonnés)',
    application: 'Mailchimp, export de l’audience : fichier des désabonnés (CSV)',
    nature: 'contacts',
    signature: SIGNATURE_MAILCHIMP_DESABONNES,
    regles: regles('contacts', 'desabonnes', {
      ...mailchimpCommun(),
      consentement: champ([], [{ type: 'constante', valeur: 'non' }]),
    }),
  },
];

/** Les sept profils fournis, en version 1. */
export function profilsEditeursDemo(creeLe: string): ProfilComplet[] {
  return DEFS.map((def) => ({
    id: def.id,
    version: 1,
    nom: def.nom,
    application: def.application,
    nature: def.nature,
    signature: def.signature,
    fourni: true,
    auteur: null,
    regles: def.regles,
    natures: [def.nature],
    creeLe,
  }));
}
